/**
 * Student Master listing — detailed roster with search highlight
 */

import { useEffect } from 'react';
import { studentMatchesHighlight } from './StudentMasterFilters';

function StatusDot({ active }) {
  const on = active !== false;
  return (
    <span className={`student-status-dot ${on ? 'student-status-dot--on' : 'student-status-dot--off'}`} title={on ? 'Active' : 'Inactive'}>
      {on ? 'Active' : 'Off'}
    </span>
  );
}

export default function StudentMasterTable({
  students,
  onEdit,
  onDelete,
  deletingStudentId,
  rosterShown,
  highlightTerm,
}) {
  const highlightCount = highlightTerm
    ? students.filter((s) => studentMatchesHighlight(s, highlightTerm)).length
    : 0;

  useEffect(() => {
    if (!highlightTerm) return;
    const el = document.querySelector('.student-roster-table tbody tr.student-search-match');
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [highlightTerm, students]);

  if (!rosterShown) {
    return (
      <div className="student-roster-placeholder">
        <p className="student-roster-placeholder-title">No roster loaded</p>
        <p className="student-roster-placeholder-text">
          Choose filters above and click <strong>Show</strong> to load students for that division.
        </p>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="admin-empty py-10">
        <p className="admin-empty-title">No students in this division</p>
        <p className="admin-empty-text">Try another division or add students via Division Master or bulk upload.</p>
      </div>
    );
  }

  return (
    <div className="student-roster-wrap">
      <div className="student-roster-summary">
        <span>{students.length} student{students.length === 1 ? '' : 's'}</span>
        {highlightTerm ? (
          <span className="student-roster-summary-highlight">
            {highlightCount} highlighted
          </span>
        ) : null}
      </div>
      <div className="table-wrap student-roster-table-wrap">
        <table className="table student-master-table student-roster-table student-roster-table--dense">
          <thead>
            <tr>
              <th className="student-col-prn">PRN</th>
              <th className="student-col-name">Name</th>
              <th className="student-col-email">Email</th>
              <th className="student-col-dept">Dept</th>
              <th className="student-col-program">Program</th>
              <th className="student-col-ay">AY</th>
              <th className="student-col-sem">Sem</th>
              <th className="student-col-div">Div</th>
              <th className="student-col-yr">Yr</th>
              <th className="student-col-sec">Sec</th>
              <th className="student-col-batch">Batch</th>
              <th className="student-col-phone">Phone</th>
              <th className="student-col-status">Status</th>
              <th className="student-roster-actions-col student-col-actions"> </th>
            </tr>
          </thead>
          <tbody>
            {students.map((s) => {
              const highlighted = studentMatchesHighlight(s, highlightTerm);
              const programLabel = s.program_name || s.program_code || '—';
              const ay = s.enrollment_academic_year || '—';
              const sem = s.enrollment_semester != null ? s.enrollment_semester : '—';
              const divisionLabel = s.division_name || '—';
              const divisionTitle = s.division_code
                ? `${divisionLabel} (${s.division_code})`
                : divisionLabel;
              const fullName = `${s.first_name || ''} ${s.last_name || ''}`.trim() || '—';
              const matchClass = highlighted ? 'student-search-match' : '';

              return (
                <tr
                  key={s.id}
                  className={matchClass || undefined}
                  data-highlighted={highlighted ? 'true' : undefined}
                >
                  <td className="student-col-prn">
                    <span className="student-roll-text" title={s.roll_number || ''}>{s.roll_number || '—'}</span>
                  </td>
                  <td className="student-col-name">
                    <span className="student-name-cell" title={fullName}>{fullName}</span>
                  </td>
                  <td className="student-col-email student-email" title={s.email || ''}>{s.email || '—'}</td>
                  <td className="student-col-dept student-dept-text">{s.department || '—'}</td>
                  <td className="student-col-program student-program-cell" title={programLabel}>{programLabel}</td>
                  <td className="student-col-ay student-cell-muted">{ay}</td>
                  <td className="student-col-sem student-cell-num">{sem}</td>
                  <td className="student-col-div student-division-cell" title={divisionTitle}>{divisionLabel}</td>
                  <td className="student-col-yr student-cell-num">{s.year_of_study ?? '—'}</td>
                  <td className="student-col-sec student-cell-num">{s.section || '—'}</td>
                  <td className="student-col-batch student-cell-muted">{s.batch || '—'}</td>
                  <td className="student-col-phone student-cell-muted">{s.contact_number || '—'}</td>
                  <td className="student-col-status"><StatusDot active={s.is_active} /></td>
                  <td className="student-col-actions">
                    <div className="student-actions">
                      <button
                        type="button"
                        onClick={() => onEdit(s)}
                        className="student-action-link student-action-link--edit"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(s.id)}
                        disabled={deletingStudentId === s.id}
                        className="student-action-link student-action-link--delete"
                      >
                        {deletingStudentId === s.id ? '…' : 'Del'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
